/* eslint-disable unicorn/consistent-function-scoping */
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DropdownComponent, InputComponent } from '@libs/ui-lib';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ImagingPreference, ImagingTools, PmsPreference, PmsTools } from '@ffd/api-client';
import { DataState, routes } from '@libs/utils';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { ActivatedRoute, Router } from '@angular/router';
import { TagPusherService, TagEvent } from '@libs/shared-services';
import { filter, take } from 'rxjs';
import { SiteSetupComponentService, StepNumberEnum } from '../site-setup/site-setup.component.service';

enum CompatibilityState {
  Loading = 'loading',
  Initial = 'initial',
  Pms = 'pms',
  Imaging = 'imaging',
  Conclusion = 'success',
}

@Component({
  selector: 'lib-compatibility',
  imports: [CommonModule, TranslateModule, SvgIconComponent, ReactiveFormsModule, DropdownComponent, InputComponent, LottieComponent],
  templateUrl: './compatibility.component.html',
  styleUrl: './compatibility.component.scss',
})
export class CompatibilityComponent implements OnInit {
  private readonly router = inject(Router);
  public readonly service = inject(SiteSetupComponentService);
  public vm = this.service.viewModel;

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tagPusherService = inject(TagPusherService);

  public DataState = DataState;
  public CompatibilityState = CompatibilityState;
  public PmsPreference = PmsPreference;
  public ImagingPreference = ImagingPreference;
  public state = signal(CompatibilityState.Loading);
  public queryParams = toSignal(this.activatedRoute.queryParams);

  public isPmsNextClicked = false;
  public isSubmitClicked = false;

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public compatibilityForm = new FormGroup({
    pms: new FormControl('', {
      validators: [Validators.required],
    }),
    pmsOther: new FormControl(''),
    // Fastpass 2 skip imaging step
    // imaging: new FormControl('', {
    //   validators: [Validators.required],
    // }),
    // imagingOther: new FormControl(''),
  });

  public ngOnInit(): void {
    this.subscribeToOtherSelect({ value: 'pms', other: 'pmsOther' });
    // this.subscribeToOtherSelect({ value: 'imaging', other: 'imagingOther' }); // Fastpass 2 skip imaging step
    this.service.compatibilityScreenData
      .pipe(
        filter((compatibilityScreenData) => {
          return (
            compatibilityScreenData.contract?.state === DataState.Fetched &&
            compatibilityScreenData.selectSiteInstallRequirements?.state === DataState.Fetched
          );
        }),
        take(1),
      )
      .subscribe((compatibilityScreenData) => {
        if (
          compatibilityScreenData.contract?.state === DataState.Fetched &&
          compatibilityScreenData.selectSiteInstallRequirements?.state === DataState.Fetched
        ) {
          this.onStateChange(CompatibilityState.Initial);
        }

        const fromForm = this.queryParams()?.['fromForm'] === 'true';
        const pmsAccepted = compatibilityScreenData.selectSiteInstallRequirements?.data?.pmsAccepted;
        if (fromForm && pmsAccepted === false) {
          // Show Conclusion screen for non-accepted PMS
          this.onStateChange(CompatibilityState.Conclusion);
        } else {
          const step = compatibilityScreenData.contract?.data?.step;

          if ((step && step > StepNumberEnum.Compatibility) || pmsAccepted === true) {
            this.onStart();
          }
        }
      });
  }

  public subscribeToOtherSelect(propertyName: { value: string; other: string }): void {
    this.compatibilityForm
      .get(propertyName.value)
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value === PmsPreference.OTHER || value === ImagingPreference.OTHER) {
          this.compatibilityForm.get(propertyName.other)?.setValidators([Validators.required]);
        } else {
          this.compatibilityForm.get(propertyName.other)?.clearValidators();
        }
        this.compatibilityForm.get(propertyName.other)?.updateValueAndValidity();
      });
  }

  public onStateChange(state: CompatibilityState): void {
    this.state.set(state);
  }

  public onPmsNextClick(): void {
    this.isPmsNextClicked = true;
    if (this.compatibilityForm.controls.pms.valid && this.compatibilityForm.controls.pmsOther.valid) {
      const selectedPmsLabel =
        this.compatibilityForm.controls.pms.value === PmsPreference.OTHER
          ? this.compatibilityForm.controls.pmsOther.value
          : this.getDropdownLabel('pms', this.vm().tools?.data?.pms);

      this.tagPusherService.pushTag({
        event: TagEvent.SiteInstallationPmsSelected,
        pms: selectedPmsLabel || undefined,
      });
      this.onSubmit();
    }
  }

  public onSubmit(): void {
    this.isSubmitClicked = true;
    const formControls = this.compatibilityForm.controls;
    if (this.compatibilityForm.valid) {
      this.service.setToolPreference({
        tools: {
          pms: formControls.pms.value as PmsPreference,
          pmsOther: formControls.pms.value === PmsPreference.OTHER ? formControls.pmsOther.value || '' : undefined,
          // Fastpass 2 skip imaging step
          // imaging: formControls.imaging.value as ImagingPreference,
          // imagingOther: formControls.imaging.value === ImagingPreference.OTHER ? formControls.imagingOther.value || '' : undefined,
        },
      });

      // Fastpass 2 skip imaging step
      // const selectedToolsLabel =
      //   formControls.imaging.value === ImagingPreference.OTHER
      //     ? formControls.imagingOther.value
      //     : this.getDropdownLabel('imaging', this.vm().tools?.data?.imaging);

      // this.tagPusherService.pushTag({
      //   event: TagEvent.SiteInstallationToolsSelected,
      //   tools: selectedToolsLabel || undefined,
      // });
      // Subscribe to site-install-requirements response to handle conditional navigation
      this.service.compatibilityScreenData
        .pipe(
          filter((compatibilityScreenData) => {
            return compatibilityScreenData.selectSiteInstallRequirements?.state === DataState.Fetched;
          }),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((compatibilityScreenData) => {
          const pmsAccepted = compatibilityScreenData.selectSiteInstallRequirements?.data?.pmsAccepted;

          if (pmsAccepted === true) {
            this.onStart();
          } else {
            // Show Conclusion screen for non-accepted PMS
            this.onStateChange(CompatibilityState.Conclusion);
          }
        });
    }
  }

  private getDropdownLabel(controlName: string, options: (PmsTools | ImagingTools)[] = []): string {
    const controlValue = this.compatibilityForm.get(controlName)?.value;
    return options.find((option) => option.value === controlValue)?.label || '';
  }

  public onStart(): void {
    // Determine the correct next step based on installation requirements
    const nextStep = this.service.determineNextStep();

    this.service.setActiveStep(nextStep);
    this.router.navigate([routes.setup, ...this.service.viewModel().stepOrder[nextStep - 1]]);
  }

  public onGoToShowCase(): void {
    this.router.navigate([routes.site, routes.showcaseSiteId, routes.home]);
  }
}
